import React, { useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { TableColumn } from 'react-data-table-component'
import { useGetPresetsQuery, useGetPresetTagsQuery } from '../../../store/ai/ai'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { Tag, TagElement } from '../../../components/Tag/Tag'
import styled from 'styled-components'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { AiFeaturePreset } from '../../../models/AiFeaturePreset'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { Button } from '../../../components/Button/Button'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { PresetFormConfigModal } from './PresetConfig/Form/PresetFormConfigModal'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { PresetTemplateConfigModal } from './PresetConfig/Template/PresetTemplateConfigModal'

const PresetIconWrapper = styled.div`
  display: inline-flex;
  padding: 0.75rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 0.25rem;
  background: var(--zocks-primary);
  color: #fff;

  i {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
  }

  img {
    width: 2rem;
    height: 2rem;
  }
`

export const PresetTab: React.FC = () => {
  useDocumentTitle('Setup')
  const accountId = useAppSelector(selectAccountId)

  const { tracked } = useTracking()

  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [selectedTag, setSelectedTag] = useState<string | null>(null)
  const { data, isFetching } = useGetPresetsQuery(
    { accountId, pageRequest: { page: page - 1, size: perPage }, tag: selectedTag },
    { refetchOnMountOrArgChange: true },
  )
  const { data: tags } = useGetPresetTagsQuery({ accountId: accountId, pageRequest: { page: 0, size: 500 } })
  const [selectedPreset, setSelectedPreset] = useState<AiFeaturePreset | null>(null)
  const [showPresetConfigModal, setShowPresetConfigModal] = useState<boolean>(false)

  const handleConfigOnClick = (preset: AiFeaturePreset) => {
    setSelectedPreset(preset)
    setShowPresetConfigModal(true)
  }

  const handleConfigModalHide = () => {
    setShowPresetConfigModal(false)
    setSelectedPreset(null)
  }

  const columns: TableColumn<AiFeaturePreset>[] = [
    {
      name: 'Preset Name',
      selector: (row) => row.name,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className='flex-grow-1 d-flex gap-3 align-items-center'>
          <PresetIconWrapper>
            <img src={row.assetUrl} alt='asset logo' />
          </PresetIconWrapper>
          <div className='button-link-medium text-decoration-none'>{row.name}</div>
        </div>
      ),
    },
    {
      name: 'Description',
      wrap: true,
      grow: 2,
      selector: (row) => row.description,
      sortable: true,
    },
    {
      name: 'Tags',
      sortable: true,
      cell: (row) => (
        <div className='d-flex gap-2 flex-wrap'>
          {row.tags.map((tag) => (
            <TagElement key={`row-tag-element-${tag}`}>{tag}</TagElement>
          ))}
        </div>
      ),
    },
    {
      sortable: true,
      width: '1.5rem',
      button: true,
      cell: (row) => (
        <>
          {row.inputParametersSchema?.properties && row.installed ? (
            <OverlayTrigger placement='bottom' overlay={<Tooltip id='button-tooltip-2'>Customize preset</Tooltip>}>
              {({ ref, ...triggerHandler }) => (
                <ScopedBlock
                  missingScopeAction={MissingScopeAction.DISABLE}
                  scope={{
                    scope: 'aifeature:list',
                    superScope: ['aifeature:admin'],
                  }}
                >
                  <Button onClick={() => handleConfigOnClick(row)} {...triggerHandler}>
                    <i ref={ref} className='bi bi-gear' />
                  </Button>
                </ScopedBlock>
              )}
            </OverlayTrigger>
          ) : null}
        </>
      ),
    },
  ]

  const handleFilterSelected = tracked(
    (tag: string | null) => setSelectedTag(tag),
    { eventName: TrackEvent.PRESET_FILTER_CLICKED },
    {
      eventDetailsExtractor: (tag) => ({ tag }),
    },
  )

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  const ConfigModal = selectedPreset?.inputParametersSchema?.properties?.template ? PresetTemplateConfigModal : PresetFormConfigModal

  return (
    <Container fluid>
      {showPresetConfigModal && selectedPreset ? <ConfigModal show={showPresetConfigModal} onHide={handleConfigModalHide} preset={selectedPreset} /> : null}
      <Row className='d-flex gap-2 zocks-mb-2.5'>
        <Col xs='auto' className='d-flex gap-3'>
          <div className='d-flex gap-1 align-items-center'>
            <i className='bi bi-funnel'></i>
            <div className='lead-small'>Filter</div>
          </div>
        </Col>
        <Col xs='auto' className='m-0 p-0'>
          <Tag selected={selectedTag === null} onClick={() => handleFilterSelected(null)}>
            All
          </Tag>
        </Col>
        {tags &&
          tags.data.map((tag) => (
            <Col key={tag} xs='auto' className='p-0 m-0'>
              <Tag selected={tag === selectedTag} onClick={() => handleFilterSelected(tag)}>
                {tag}
              </Tag>
            </Col>
          ))}
      </Row>
      <Row>
        <Col className='p-0'>
          <ZocksDataTable
            columns={columns}
            data={data?.data ?? []}
            progressPending={isFetching}
            pagination
            paginationServer
            paginationTotalRows={data?.page.totalElements}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
          />
        </Col>
      </Row>
    </Container>
  )
}
